import { createContext, useContext, useRef, useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

const ConfirmCtx = createContext(() => Promise.resolve(false));

export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const [typedValue, setTypedValue] = useState("");
  const resolver = useRef(null);

  const confirm = useCallback((o = {}) => {
    setOpts(o);
    setTypedValue("");
    setOpen(true);
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = (val) => {
    setOpen(false);
    if (resolver.current) { resolver.current(val); resolver.current = null; }
  };

  const needsTypedMatch = !!opts.typeToConfirm;
  const isConfirmDisabled = needsTypedMatch && typedValue.trim() !== opts.typeToConfirm;

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) settle(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="confirm-title">{opts.title || "Are you sure?"}</AlertDialogTitle>
            {opts.body && <AlertDialogDescription data-testid="confirm-body">{opts.body}</AlertDialogDescription>}
          </AlertDialogHeader>
          {needsTypedMatch && (
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                Type <span className="font-semibold text-foreground">{opts.typeToConfirm}</span> to confirm.
              </p>
              <Input
                autoFocus
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                placeholder={opts.typeToConfirm}
                data-testid="confirm-type-input"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel" onClick={() => settle(false)}>
              {opts.cancelText || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-ok"
              disabled={isConfirmDisabled}
              onClick={(e) => { if (isConfirmDisabled) { e.preventDefault(); return; } settle(true); }}
              className={opts.destructive ? "bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:pointer-events-none" : ""}
            >
              {opts.confirmText || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}
